/* eslint-disable security/detect-object-injection */
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  InputValidationError,
  NotEnoughBalance,
} from 'src/app/shared/errors/reward-service/reward-service-errors';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { SysadminMonthPointBalanceResponseDTO } from 'src/app/shared/models/dtos/responses/sysadmin-month-point-balance-response-dto/sysadmin-month-point-balance-response-dto';
import { minimumTrimmedLength } from 'src/app/shared/validators/registration-validators';
import { Subscription } from 'rxjs';
import { BusinessFrozenException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';

@Component({
  selector: 'app-modal',
  templateUrl: './point-transaction-modal.component.html',
  styleUrls: ['./point-transaction-modal.component.scss'],
})

/**
 * Transaction points modal to add or remove points
 */
export class PointTransactionModalComponent implements OnInit {
  private static readonly NOTES_BLOCK_TEST = /['\\><=+@`{&]/;
  /**
   * Validator function to check if the Notes field contains restricted special characters.
   * Returns a validation error object `{ notesDisallowed: true }` when restricted
   * characters are present, otherwise returns `null`.
   *
   * @private
   * @param {AbstractControl} c - The form control being validated.
   * @return {ValidationErrors | null} - Validation error if restricted characters found, else null.
   */
  private static notesValidator(c: AbstractControl): ValidationErrors | null {
    const v = (c.value ?? '') as string;
    return PointTransactionModalComponent.NOTES_BLOCK_TEST.test(v)
      ? { notesDisallowed: true }
      : null;
  }

  transactionType = 'ADD';

  addOrRemovePoints = {
    addPoints: true,
    removePoints: false,
  };

  snackBarLabels = {
    success: 'success',
    error: 'error',
    notEnoughBalance: 'not-enough-balance',
    inputValidationError: 'invalid-notesDisallowed',
    businessFrozen: 'business-is-frozen-exception',
  };

  keyErrors: string[] = [
    'businessBalance',
    'totalBalancePerMonth',
    'totalBalancePerYear',
  ];

  form = new FormGroup({
    transactionValue: new FormControl<string>('', [
      Validators.required,
      Validators.pattern('^[1-9][0-9]*$'),
      Validators.maxLength(7),
      Validators.minLength(1),
      this.transactionValuesValidator(),
    ]),
    transactionNote: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(300),
      minimumTrimmedLength(3),
      PointTransactionModalComponent.notesValidator, // validation-only
    ]),
    confirm: new FormControl<boolean>(false, [Validators.requiredTrue]),
  });

  @Output() pointsChanged = new EventEmitter<number>();
  @Input() pointSummary!: PointSummaryDTO;

  loading = false;
  exceededLimitsLabelString = '';
  private statusSub?: Subscription;
  language = '';

  /**
   * @constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PointTransactionModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      totalPoints: number;
      businessId: number;
      family: any;
      pointBalance?: SysadminMonthPointBalanceResponseDTO;
    },
    private readonly translationService: TranslationService,
    private snackBarService: SnackBarService,
    public rewardService: RewardService
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.language = this.translationService.getActiveLang();
    this.translationService.translateObject(this.snackBarLabels);

    this.statusSub = this.form.statusChanges.subscribe(() => {
      this.updateExceededLimitsLabelString();
    });

    this.updateExceededLimitsLabelString();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
  }

  /**
   * Action to close the dialog
   */
  onCloseClick() {
    this.dialogRef.close();
  }

  /**
   * Action to close the dialog
   */
  onSuccessClick() {
    this.loading = true;

    const transactionNote =
      this.form.value.transactionNote?.trim().replace(/\s+/g, ' ') ?? '';

    const response = {
      totalPoints: this.calculateAfterTransaction(),
      operation: this.transactionType,
      inputValue: this.form.value.transactionValue,
    };

    if (this.transactionType === 'ADD') {
      this.rewardService
        .createPointsTransaction(
          this.data.businessId,
          Number(this.form.value.transactionValue),
          transactionNote,
          this.data.family.id
        )
        .subscribe({
          next: () => {
            this.snackBarService.success(this.snackBarLabels.success);
            this.pointsChanged.emit(response.totalPoints);
            this.data.pointBalance!.businessBalance -= Number(
              response.inputValue
            );
            this.data.pointBalance!.totalBalancePerMonth -= Number(
              response.inputValue
            );
            this.data.pointBalance!.totalBalancePerYear -= Number(
              response.inputValue
            );
            this.dialogRef.close(response);
          },
          error: (err) => {
            this.loading = false;
            if (err instanceof NotEnoughBalance) {
              return this.snackBarService.warning(
                this.snackBarLabels.notEnoughBalance
              );
            }
            if (err instanceof InputValidationError) {
              return this.snackBarService.warning(
                this.snackBarLabels.inputValidationError
              );
            }

            if (err instanceof BusinessFrozenException) {
              this.snackBarService.error(this.snackBarLabels.businessFrozen);
            } else {
              this.snackBarService.error(this.snackBarLabels.error);
            }
          },
        });
    } else {
      this.rewardService
        .removePointsTransaction(
          this.data.businessId,
          Number(this.form.value.transactionValue),
          transactionNote,
          this.data.family.id
        )
        .subscribe({
          next: () => {
            this.snackBarService.success(this.snackBarLabels.success);
            this.pointsChanged.emit(response.totalPoints);
            this.dialogRef.close(response);
          },
          error: (error) => {
            this.loading = false;
            if (error instanceof BusinessFrozenException) {
              this.snackBarService.error(this.snackBarLabels.businessFrozen);
            } else {
              this.snackBarService.error(this.snackBarLabels.error);
            }
          },
        });
    }
  }

  /**
   * Action to close the dialog
   */
  onFailClick() {
    this.dialogRef.close(MatDialogResponse.FAIL);
  }

  /**
   * Sets selector to add points
   */
  addPoints() {
    this.unsetSelector();
    this.addOrRemovePoints.addPoints = true;
    this.transactionType = 'ADD';
  }

  /**
   * Sets selector to remove points
   */
  removePoints() {
    this.unsetSelector();
    this.addOrRemovePoints.removePoints = true;
    this.transactionType = 'SPEND';
  }

  /**
   * unsets selector
   */
  private unsetSelector() {
    this.addOrRemovePoints.removePoints = false;
    this.addOrRemovePoints.addPoints = false;
  }

  /**
   * Calculation based on the selected button
   * @return {number}
   */
  calculateAfterTransaction(): number {
    const toCalculate = Number(this.form.value.transactionValue);
    let afterTransactionValue = 0;

    if (this.addOrRemovePoints.addPoints) {
      afterTransactionValue = this.data.totalPoints + toCalculate;
    } else {
      afterTransactionValue = this.data.totalPoints - toCalculate;
    }
    return afterTransactionValue;
  }

  /**
   * Checks if create point transaction button is enabled based on sysadmin and business available points
   * @return {boolean}
   */
  isBalanceNotEnough() {
    if (this.addOrRemovePoints.removePoints) {
      return false;
    }
    if (!this.data.pointBalance) {
      return true;
    }
    return (
      this.data.pointBalance?.businessBalance -
        Number(this.form.value.transactionValue) <
        0 ||
      this.data.pointBalance?.totalBalancePerMonth -
        Number(this.form.value.transactionValue) <
        0 ||
      this.data.pointBalance?.totalBalancePerYear -
        Number(this.form.value.transactionValue) <
        0
    );
  }

  /**
   * Check if the value is a number
   * @param {number} value
   * @return {boolean}
   */
  showNumber(value: number) {
    return value !== this.data.totalPoints && !Number.isNaN(value);
  }

  /**
   * Disable button
   * @return {boolean}
   */
  disableForm() {
    return (
      this.form.invalid ||
      this.calculateAfterTransaction() < 0 ||
      this.isBalanceNotEnough() ||
      this.loading
    );
  }

  /**
   * Validator function to check whether the transaction value exceeds the available amounts.
   *
   * @return {ValidationErrors | null} - Validation error if restricted characters found, else null.
   */
  transactionValuesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = control.value;

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return null;
      }

      const amount = Number(rawValue);
      if (Number.isNaN(amount)) {
        return { invalidNumber: true };
      }

      const limits = [-1, -1, -1];
      if (this.data.pointBalance) {
        if (this.data.pointBalance.businessBalance !== null) {
          limits[0] = this.data.pointBalance.businessBalance;
        }
        if (this.data.pointBalance.totalBalancePerMonth !== null) {
          limits[1] = this.data.pointBalance.totalBalancePerMonth;
        }
        if (this.data.pointBalance.totalBalancePerYear !== null) {
          limits[2] = this.data.pointBalance.totalBalancePerYear;
        }
      }

      const response: ValidationErrors = {};
      for (let i = 0; i < limits.length; i++) {
        if (limits[i] !== -1 && amount > limits[i]) {
          response[this.keyErrors[i]] = true;
        }
      }

      if (Object.keys(response).length > 0) {
        return response;
      }

      return null;
    };
  }

  /**
   * Retrieve error keys
   */
  get exceededLimitKeys(): string[] {
    const errors = this.form.get('transactionValue')?.errors;
    if (!errors) {
      return [];
    }

    const keys: string[] = [];
    for (const key of this.keyErrors) {
      if (Object.keys(errors).includes(key) && errors[key]) {
        keys.push(key);
      }
    }

    return keys;
  }

  /**
   * Join labels
   *
   * @param {string[]} items - labels translated
   * @param {string} andWord - and word translated
   * @return {string}
   * @private
   */
  private joinWithCommaAndAnd(items: string[], andWord: string): string {
    if (items.length === 0) {
      return '';
    }
    if (items.length === 1) {
      return items[0];
    }
    if (items.length === 2) {
      return `${items[0]} ${andWord} ${items[1]}`;
    }

    const allButLast = items.slice(0, -1).join(', ');
    const last = items[items.length - 1];
    return `${allButLast} ${andWord} ${last}`;
  }

  /**
   * Update error label error for transaction value
   *
   * @private
   */
  private async updateExceededLimitsLabelString(): Promise<void> {
    const keys = this.exceededLimitKeys;

    if (!keys.length) {
      this.exceededLimitsLabelString = '';
      return;
    }

    const obj: { [key: string]: string } = {
      and: 'limits.and',
    };

    keys.forEach((key: string) => {
      obj[key] = `limits.${key}`;
    });

    await this.translationService.translateObject(obj);

    const labels = keys.map((key: string) => obj[key]);
    const andWord = obj['and'];

    this.exceededLimitsLabelString = this.joinWithCommaAndAnd(labels, andWord);
  }
}
